import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import BusinessIcon from '@mui/icons-material/Business';
import InsightsIcon from '@mui/icons-material/Insights';
import PublicIcon from '@mui/icons-material/Public';
import TimelineIcon from '@mui/icons-material/Timeline';

import {
  buildWorkspaceDescriptorChips,
  buildWorkspaceDescriptorSidebarItems,
  normalizeWorkspaceDescriptor,
  normalizeWorkspaceDescriptorRoute,
} from '../../../components/common/AnalyticsWorkspace';
import { getCompanyInfo } from '../../../utils/companyHelpers';
import { ACCESS_POLICY_IDS } from '../../../configs/accessPolicies';

export const SI_HIRING_VIEW_STORAGE_KEY = 'marketInsightsSIHiringView';
export const SI_HIRING_VIEW_EVENT_NAME = 'marketInsightsSIHiringViewChange';

export const SI_HIRING_SECTIONS = {
  MARKET: 'market',
  CATEGORY: 'category',
  VENDOR: 'vendor',
  COMPANY: 'company',
};

export const SI_HIRING_VIEWS = {
  TRENDS: 'trends',
  DASHBOARD: 'dashboard',
  POSTINGS: 'postings',
};

export const SI_HIRING_RENDER_VIEWS = {
  MARKET: 'market',
  CATEGORY: 'category',
  VENDOR: 'vendor',
  COMPANY: 'detail',
};

export const PARTNER_HIRING_VIEWS = {
  DASHBOARD: SI_HIRING_VIEWS.DASHBOARD,
  POSTINGS: SI_HIRING_VIEWS.POSTINGS,
};

export const PARTNER_HIRING_VIEW_IDS = [
  PARTNER_HIRING_VIEWS.DASHBOARD,
];

export const SI_HIRING_VIEW_LABELS = {
  [SI_HIRING_VIEWS.TRENDS]: 'Partner Trends',
  [SI_HIRING_VIEWS.DASHBOARD]: 'Partner Trends',
  [SI_HIRING_VIEWS.POSTINGS]: 'Partner Trends',
};

export const PARTNER_HIRING_VIEW_LABELS = {
  [PARTNER_HIRING_VIEWS.DASHBOARD]: 'Partner Trends',
  [PARTNER_HIRING_VIEWS.POSTINGS]: 'Partner Trends',
};

const normalizeTicker = (ticker) => String(ticker || '').trim().toUpperCase();

export const normalizePartnerHiringView = (view) => (
  PARTNER_HIRING_VIEW_IDS.includes(view) ? view : PARTNER_HIRING_VIEWS.DASHBOARD
);

export const getSIHiringWorkspaceCompanyName = (ticker) => {
  const normalizedTicker = normalizeTicker(ticker);
  if (!normalizedTicker) return null;
  const info = getCompanyInfo(normalizedTicker);
  return info.companyName || normalizedTicker;
};

export const getSIHiringRenderView = (sectionId) => {
  if (sectionId === SI_HIRING_SECTIONS.MARKET) return SI_HIRING_RENDER_VIEWS.MARKET;
  if (sectionId === SI_HIRING_SECTIONS.CATEGORY) return SI_HIRING_RENDER_VIEWS.CATEGORY;
  if (sectionId === SI_HIRING_SECTIONS.COMPANY) return SI_HIRING_RENDER_VIEWS.COMPANY;
  return SI_HIRING_RENDER_VIEWS.VENDOR;
};

export const getSIHiringCompanyView = (viewId) => normalizePartnerHiringView(viewId);

export const buildSIHiringPath = ({
  sectionId = SI_HIRING_SECTIONS.CATEGORY,
  viewId,
  ticker,
} = {}) => {
  const section = Object.values(SI_HIRING_SECTIONS).includes(sectionId)
    ? sectionId
    : SI_HIRING_SECTIONS.CATEGORY;
  const defaultView = section === SI_HIRING_SECTIONS.COMPANY
    ? SI_HIRING_VIEWS.DASHBOARD
    : SI_HIRING_VIEWS.TRENDS;
  const allowedViews = section === SI_HIRING_SECTIONS.COMPANY
    ? [SI_HIRING_VIEWS.DASHBOARD]
    : [SI_HIRING_VIEWS.TRENDS];
  const view = allowedViews.includes(viewId) ? viewId : defaultView;
  const base = `/sihiring/${section}/${view}`;
  const normalizedTicker = normalizeTicker(ticker);
  if (section !== SI_HIRING_SECTIONS.COMPANY || !normalizedTicker) return base;
  const params = new URLSearchParams();
  params.set('ticker', normalizedTicker);
  return `${base}?${params.toString()}`;
};

export const buildPartnerHiringPath = ({ ticker, view = PARTNER_HIRING_VIEWS.DASHBOARD } = {}) => {
  const normalizedTicker = normalizeTicker(ticker).toLowerCase();
  const normalizedView = normalizePartnerHiringView(view);
  const params = new URLSearchParams();
  params.set('view', normalizedView);
  return `/company/${normalizedTicker}/partnerhiring?${params.toString()}`;
};

export const buildLegacySIHiringRedirectPath = ({ view, ticker } = {}) => {
  const normalizedView = view === 'dashboard' ? 'vendor' : view;
  if (normalizedView === 'detail') {
    return buildSIHiringPath({
      sectionId: SI_HIRING_SECTIONS.COMPANY,
      viewId: SI_HIRING_VIEWS.DASHBOARD,
      ticker,
    });
  }
  if (normalizedView === SI_HIRING_SECTIONS.CATEGORY) {
    return buildSIHiringPath({ sectionId: SI_HIRING_SECTIONS.CATEGORY });
  }
  if (normalizedView === SI_HIRING_SECTIONS.MARKET) {
    return buildSIHiringPath({ sectionId: SI_HIRING_SECTIONS.MARKET });
  }
  return buildSIHiringPath({ sectionId: SI_HIRING_SECTIONS.VENDOR });
};

const SI_TREND_PANEL_SKELETON = {
  kind: 'chart',
  preset: 'barTrend',
  variant: 'stackedBar',
  height: '58vh',
  width: '90%',
  minHeight: 420,
};

const SI_SUMMARY_TABLE_SKELETON = {
  kind: 'table',
  preset: 'signal',
  rows: 12,
  columns: 10,
  tableWidth: 1180,
  height: 320,
};

const PARTNER_TREND_PANEL_SKELETON = {
  kind: 'chart',
  preset: 'barTrend',
  variant: 'stackedBar',
  height: '36vh',
  width: '90%',
  minHeight: 260,
};

const PARTNER_SUMMARY_TABLE_SKELETON = {
  kind: 'table',
  preset: 'signal',
  rows: 10,
  columns: 9,
  tableWidth: 1032,
  height: 300,
};

const TREND_VIEW = {
  id: SI_HIRING_VIEWS.TRENDS,
  label: SI_HIRING_VIEW_LABELS[SI_HIRING_VIEWS.TRENDS],
  description: 'Trend and summary',
  icon: TimelineIcon,
  accessPolicyId: ACCESS_POLICY_IDS.PARTNER_TRENDS_DASHBOARD,
  requiredData: 'siHiring',
  controls: ['category', 'ticker', 'weeks', 'type'],
  layout: 'chartTable',
  panels: [
    {
      id: 'trend',
      kind: 'chart',
      skeleton: SI_TREND_PANEL_SKELETON,
    },
    {
      id: 'summary',
      kind: 'table',
      skeleton: SI_SUMMARY_TABLE_SKELETON,
    },
  ],
  visualizations: ['stackedBar', 'table'],
  tableColumns: ['company', 'ticker', 'metricValue', 'trendDelta', 'percent'],
};

export const SI_HIRING_WORKSPACE_DESCRIPTOR = {
  id: 'siHiring',
  title: 'SI Hiring',
  description: 'System integrator hiring trends across vendor ecosystems.',
  badge: 'Preview',
  icon: WorkOutlineIcon,
  defaultSectionId: SI_HIRING_SECTIONS.CATEGORY,
  defaultViewId: SI_HIRING_VIEWS.TRENDS,
  sections: [
    {
      id: SI_HIRING_SECTIONS.CATEGORY,
      label: 'Market',
      description: 'Segmented and total SI market trends',
      icon: PublicIcon,
      views: [TREND_VIEW],
    },
    {
      id: SI_HIRING_SECTIONS.VENDOR,
      label: 'Vendor',
      description: 'Vendor-tagged SI demand',
      icon: InsightsIcon,
      views: [TREND_VIEW],
    },
    {
      id: SI_HIRING_SECTIONS.COMPANY,
      label: 'Company Detail',
      description: 'Single-vendor partner demand',
      icon: BusinessIcon,
      views: [
        {
          id: SI_HIRING_VIEWS.DASHBOARD,
          label: SI_HIRING_VIEW_LABELS[SI_HIRING_VIEWS.DASHBOARD],
          description: 'Trend and summary',
          icon: TimelineIcon,
          accessPolicyId: ACCESS_POLICY_IDS.PARTNER_TRENDS_DASHBOARD,
          requiredData: 'partnerHiring',
          controls: ['companySearch', 'weeks', 'type'],
          layout: 'chartTable',
          panels: [
            {
              id: 'trend',
              kind: 'chart',
              skeleton: PARTNER_TREND_PANEL_SKELETON,
            },
            {
              id: 'summary',
              kind: 'table',
              skeleton: PARTNER_SUMMARY_TABLE_SKELETON,
            },
          ],
          visualizations: ['stackedBar', 'table'],
          tableColumns: ['company', 'category', 'metricValue', 'percent'],
        },
      ],
    },
  ],
};

export const SI_HIRING_WORKSPACE_CONFIG = {
  ...normalizeWorkspaceDescriptor(SI_HIRING_WORKSPACE_DESCRIPTOR),
  buildPath: ({ sectionId, viewId, ticker }) => buildSIHiringPath({ sectionId, viewId, ticker }),
  resolveRoute: ({ sectionId, viewId }) => normalizeWorkspaceDescriptorRoute({
    descriptor: SI_HIRING_WORKSPACE_DESCRIPTOR,
    sectionId,
    viewId,
  }),
  shouldRedirect: ({ sectionId, viewId, resolvedRoute }) => (
    sectionId !== resolvedRoute.sectionId || viewId !== resolvedRoute.viewId
  ),
  getExtraChips: ({ routeState = {}, resolvedRoute }) => {
    const normalizedTicker = normalizeTicker(routeState.ticker);
    if (resolvedRoute.sectionId !== SI_HIRING_SECTIONS.COMPANY || !normalizedTicker) return [];
    return [{ label: getSIHiringWorkspaceCompanyName(normalizedTicker) }];
  },
  getChips: ({ routeState = {}, resolvedRoute }) => buildWorkspaceDescriptorChips({
    descriptor: SI_HIRING_WORKSPACE_DESCRIPTOR,
    sectionId: resolvedRoute.sectionId,
    viewId: resolvedRoute.viewId,
    routeState,
    extraChips: SI_HIRING_WORKSPACE_CONFIG.getExtraChips({ routeState, resolvedRoute }),
  }),
};

export const PARTNER_HIRING_WORKSPACE_DESCRIPTOR = {
  id: 'partnerHiring',
  title: 'Partner Hiring',
  description: 'Systems integrator demand for a single vendor ecosystem.',
  badge: 'Preview',
  icon: WorkOutlineIcon,
  defaultSectionId: 'partner-hiring',
  defaultViewId: PARTNER_HIRING_VIEWS.DASHBOARD,
  sections: [
    {
      id: 'partner-hiring',
      label: 'Partner Hiring',
      description: 'Single-vendor partner demand',
      icon: WorkOutlineIcon,
      views: [
        {
          id: PARTNER_HIRING_VIEWS.DASHBOARD,
          label: PARTNER_HIRING_VIEW_LABELS[PARTNER_HIRING_VIEWS.DASHBOARD],
          description: 'Trend and summary',
          icon: TimelineIcon,
          accessPolicyId: ACCESS_POLICY_IDS.PARTNER_TRENDS_DASHBOARD,
          requiredData: 'partnerHiring',
          controls: ['weeks', 'type'],
          layout: 'chartTable',
          panels: [
            {
              id: 'trend',
              kind: 'chart',
              skeleton: PARTNER_TREND_PANEL_SKELETON,
            },
            {
              id: 'summary',
              kind: 'table',
              skeleton: PARTNER_SUMMARY_TABLE_SKELETON,
            },
          ],
          visualizations: ['stackedBar', 'table'],
          tableColumns: ['company', 'category', 'metricValue', 'percent'],
        },
      ],
    },
  ],
};

export const PARTNER_HIRING_WORKSPACE_CONFIG = {
  ...normalizeWorkspaceDescriptor(PARTNER_HIRING_WORKSPACE_DESCRIPTOR),
  sectionId: 'partner-hiring',
};

export const buildSIHiringWorkspaceSidebarItems = ({ ticker } = {}) => (
  buildWorkspaceDescriptorSidebarItems({
    descriptor: SI_HIRING_WORKSPACE_DESCRIPTOR,
    routeState: { ticker },
    buildPath: ({ sectionId, viewId, ticker: routeTicker }) => buildSIHiringPath({ sectionId, viewId, ticker: routeTicker }),
  })
);

export const buildPartnerHiringWorkspaceSidebarItems = ({ ticker } = {}) => (
  buildWorkspaceDescriptorSidebarItems({
    descriptor: PARTNER_HIRING_WORKSPACE_DESCRIPTOR,
    routeState: { ticker },
    buildPath: ({ viewId, ticker: routeTicker }) => buildPartnerHiringPath({ ticker: routeTicker, view: viewId }),
  })
);

export const buildSIHiringWorkspaceChips = ({ sectionId, viewId, ticker } = {}) => {
  const resolvedRoute = SI_HIRING_WORKSPACE_CONFIG.resolveRoute({ sectionId, viewId });
  return SI_HIRING_WORKSPACE_CONFIG.getChips({
    routeState: { ticker },
    resolvedRoute,
  });
};

export const buildPartnerHiringWorkspaceChips = ({ ticker, view } = {}) => {
  const normalizedView = normalizePartnerHiringView(view);
  return buildWorkspaceDescriptorChips({
    descriptor: PARTNER_HIRING_WORKSPACE_DESCRIPTOR,
    sectionId: PARTNER_HIRING_WORKSPACE_CONFIG.defaultSectionId,
    viewId: normalizedView,
    routeState: { ticker },
  });
};
